/**
 * Hook to check if the current user already has access to a cart product.
 *
 * Check timing varies by product type:
 * - Digital tool: synchronous check from Redux user roles (immediate)
 * - Free e-learning: API call on mount (automatic)
 * - Paid e-learning: API call on form submit (imperative via `checkAccess`)
 *
 * Fail-open: if the check fails (network error, etc.), allows purchase.
 */

import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { List } from 'immutable';
import config from 'react-global-configuration';

import useCurrentUser from '../../../../hooks/useCurrentUser';
import { getProductType, type ProductType } from './digitalProductHelpers';

export interface AccessCheckResult {
  /** Whether the user is blocked from purchasing (already has access). */
  isBlocked: boolean;
  /** Machine-readable reason code (USER_ALREADY_ENROLLED, USER_ALREADY_HAS_ROLE). */
  reason: string | null;
  /** The detected product type. */
  productType: ProductType;
}

interface UseProductAccessCheckReturn {
  /** Call on submit for paid e-learning. Resolves with the access check result. */
  checkAccess: (
    orderForSomeoneElse: boolean,
    quantity: number,
  ) => Promise<AccessCheckResult>;
  /** Whether any check (auto or submit) is currently in progress. */
  isChecking: boolean;
  /** The effective result from any check source, or null if pending. */
  result: AccessCheckResult | null;
  /** Reset the API result (used to dismiss banner for paid e-learning). */
  resetResult: () => void;
}

interface AccessCheckApiResponse {
  has_access: boolean;
  product_type: string;
  sku: string;
  reason: string | null;
}

const VALANALYSE_ROLE = 'valanalyse_user';

const NOT_BLOCKED: AccessCheckResult = {
  isBlocked: false,
  reason: null,
  productType: 'unknown',
};

/**
 * Gets the backend API base URL from configuration.
 */
const getApiUrl = (): string => {
  const baseUrl = config.get('API_URL') || '/api';
  if (baseUrl.startsWith('//')) {
    return baseUrl.replace('//', 'http://');
  }
  return baseUrl;
};

/**
 * Extracts the SKU from the first cart product.
 */
const getCartProductSku = (
  cartProducts: List<any> | undefined,
): string => {
  if (!cartProducts || cartProducts.size === 0) return '';

  const firstProduct = cartProducts.get(0);
  return (firstProduct?.getIn(
    ['relationships', 'purchased_entity', 'attributes', 'sku'],
    '',
  ) as string) || '';
};

/**
 * Builds the enrollment check API URL for a given SKU.
 */
const buildAccessCheckUrl = (sku: string): string => {
  const encoded = encodeURIComponent(sku);
  return `${getApiUrl()}/api/commerce/product-access-check?sku=${encoded}&_format=json`;
};

/**
 * Fetches enrollment status from the backend API.
 * Returns the parsed AccessCheckResult or null on failure (fail-open).
 */
const fetchEnrollmentCheck = async (
  sku: string,
  productType: ProductType,
): Promise<AccessCheckResult> => {
  const response = await fetch(buildAccessCheckUrl(sku), {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (response.ok) {
    const data: AccessCheckApiResponse = await response.json();
    return {
      isBlocked: data.has_access === true,
      reason: data.reason ?? null,
      productType,
    };
  }

  // Non-OK response: fail-open.
  return { isBlocked: false, reason: null, productType };
};

/**
 * Checks product access with timing that varies by product type.
 *
 * - **Digital tool**: instant `useMemo` from Redux user roles (no API call).
 * - **Free e-learning**: `useEffect` on mount calls backend API automatically.
 * - **Paid e-learning**: call `checkAccess(orderForSomeoneElse, quantity)` on
 *   form submit — needs form values to decide whether to skip the check.
 * - On any error: fails open (returns not blocked).
 *
 * @param cartProducts - ImmutableJS List of cart products.
 * @param isPaidProduct - Whether the cart product has a price > 0.
 */
export default function useProductAccessCheck(
  cartProducts: List<any> | undefined,
  isPaidProduct: boolean,
): UseProductAccessCheckReturn {
  const user = useCurrentUser();
  const [isApiChecking, setIsApiChecking] = useState(false);
  const [apiResult, setApiResult] = useState<AccessCheckResult | null>(null);

  const productType = useMemo(
    () => getProductType(cartProducts),
    [cartProducts],
  );

  const sku = useMemo(
    () => getCartProductSku(cartProducts),
    [cartProducts],
  );

  // ── Digital tool: synchronous check from Redux user roles ──────────
  const digitalToolResult = useMemo<AccessCheckResult | null>(() => {
    if (productType !== 'digital_tool') return null;
    const hasRole = user?.roles?.includes(VALANALYSE_ROLE) ?? false;
    return {
      isBlocked: hasRole,
      reason: hasRole ? 'USER_ALREADY_HAS_ROLE' : null,
      productType,
    };
  }, [productType, user?.roles]);

  // ── Reset API result when the product changes (e.g. new checkout) ───
  useEffect(() => {
    setApiResult(null);
    setIsApiChecking(false);
  }, [productType, sku, isPaidProduct]);

  // ── Free e-learning: auto-check enrollment on mount ────────────────
  useEffect(() => {
    if (productType !== 'e_learning' || isPaidProduct || !sku) return undefined;

    let cancelled = false;
    setIsApiChecking(true);

    fetchEnrollmentCheck(sku, productType)
      .then((checkResult) => {
        if (!cancelled) setApiResult(checkResult);
      })
      .catch(() => {
        // Fail-open: allow purchase on network/API error.
        if (!cancelled) {
          setApiResult({ isBlocked: false, reason: null, productType });
        }
      })
      .finally(() => {
        if (!cancelled) setIsApiChecking(false);
      });

    return () => { cancelled = true; };
  }, [productType, isPaidProduct, sku]);

  // ── Paid e-learning: imperative check on form submit ───────────────
  const checkAccess = useCallback(async (
    orderForSomeoneElse: boolean,
    quantity: number,
  ): Promise<AccessCheckResult> => {
    // Skip check when ordering for others or multiple quantity.
    if (orderForSomeoneElse || quantity >= 2) {
      const checkResult: AccessCheckResult = {
        isBlocked: false, reason: null, productType,
      };
      setApiResult(checkResult);
      return checkResult;
    }

    // Call backend API for paid e-learning, self, qty 1.
    if (sku) {
      setIsApiChecking(true);
      try {
        const checkResult = await fetchEnrollmentCheck(sku, productType);
        setApiResult(checkResult);
        setIsApiChecking(false);
        return checkResult;
      } catch {
        // Fail-open: allow purchase on network/API error.
      }
      setIsApiChecking(false);
    }

    const checkResult: AccessCheckResult = {
      ...NOT_BLOCKED, productType,
    };
    setApiResult(checkResult);
    return checkResult;
  }, [productType, sku]);

  // ── Reset (dismiss banner for paid e-learning) ─────────────────────
  const resetResult = useCallback(() => {
    setApiResult(null);
  }, []);

  // ── Derived state ──────────────────────────────────────────────────
  // Effective result: digital tool (sync) takes priority over API result.
  const result = digitalToolResult ?? apiResult;

  // Loading: true during API calls OR while free e-learning auto-check
  // hasn't completed yet (prevents form flash before API returns).
  const autoCheckPending = productType === 'e_learning'
    && !isPaidProduct && !!sku && apiResult === null;
  const isChecking = isApiChecking || autoCheckPending;

  return {
    checkAccess, isChecking, result, resetResult,
  };
}
